import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Save, Send, Mail, Server, CheckCircle, XCircle, AlertCircle, Edit3, RotateCcw, ChevronDown, ChevronUp, Eye, X } from 'lucide-react';
import axios from 'axios';

const EmailSMTP = () => {
  const [config, setConfig] = useState({
    host: '',
    port: 465,
    username: '',
    password: '',
    from_name: '',
    use_ssl: true,
    enabled: false,
    is_active: false
  });

  const [testEmail, setTestEmail] = useState({
    to_email: '',
    subject: 'Test Email',
    message: 'This is a test email to verify SMTP configuration.',
    template_type: ''
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  
  // Template Editor State
  const [templates, setTemplates] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(null);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({ subject: '', body: '' });

  useEffect(() => {
    fetchConfig();
    fetchTemplates();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/email/config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (error) {
      showAlert('error', 'Failed to load SMTP configuration');
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config.host || !config.username || !config.password) {
      showAlert('error', 'Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/email/config`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showAlert('success', 'SMTP configuration saved successfully!');
      }
    } catch (error) {
      showAlert('error', error.response?.data?.detail || 'Failed to save configuration');
      console.error('Error saving config:', error);
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail.to_email) {
      showAlert('error', 'Please enter recipient email address');
      return;
    }

    setTestLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build URL with template_type query param if selected
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/email/test`;
      if (testEmail.template_type) {
        url += `?template_type=${testEmail.template_type}`;
      }
      
      const response = await axios.post(
        url,
        {
          to_email: testEmail.to_email,
          subject: testEmail.subject,
          message: testEmail.message
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showAlert('success', `Test email sent successfully to ${testEmail.to_email}!`);
        setTestEmail({ ...testEmail, to_email: '' });
      } else {
        showAlert('error', response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      showAlert('error', error.response?.data?.detail || 'Failed to send test email');
      console.error('Error sending test email:', error);
    } finally {
      setTestLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };

  // Template Functions
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/email/templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      showAlert('error', 'Failed to load email templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveTemplate = async (templateType) => {
    const template = templates[templateType];
    
    if (!template.subject || !template.body) {
      showAlert('error', 'Subject dan Body tidak boleh kosong');
      return;
    }

    setSavingTemplate(templateType);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/email/templates/${templateType}`,
        template,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showAlert('success', `Template "${getTemplateName(templateType)}" berhasil disimpan!`);
        fetchTemplates(); // Reload templates
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showAlert('error', error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(null);
    }
  };

  const resetTemplate = async (templateType) => {
    if (!window.confirm(`Apakah Anda yakin ingin mereset template "${getTemplateName(templateType)}" ke default?`)) {
      return;
    }

    setSavingTemplate(templateType);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/email/templates/${templateType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showAlert('success', `Template "${getTemplateName(templateType)}" berhasil direset ke default!`);
        fetchTemplates(); // Reload templates
      }
    } catch (error) {
      console.error('Error resetting template:', error);
      showAlert('error', error.response?.data?.detail || 'Failed to reset template');
    } finally {
      setSavingTemplate(null);
    }
  };

  const updateTemplate = (templateType, field, value) => {
    setTemplates({
      ...templates,
      [templateType]: {
        ...templates[templateType],
        [field]: value
      }
    });
  };

  const toggleTemplateExpand = (templateType) => {
    setExpandedTemplate(expandedTemplate === templateType ? null : templateType);
  };

  const getTemplateName = (type) => {
    const names = {
      welcome: '✉️ Welcome Email',
      invoice: '📄 Invoice Email',
      reminder: '⏰ Payment Reminder',
      overdue: '⚠️ Payment Overdue',
      confirmation: '✅ Payment Confirmation'
    };
    return names[type] || type;
  };

  const getTemplateColor = (type) => {
    const colors = {
      welcome: 'green',
      invoice: 'blue',
      reminder: 'yellow',
      overdue: 'red',
      confirmation: 'purple'
    };
    return colors[type] || 'gray';
  };

  const getSampleData = (templateType) => {
    const samples = {
      welcome: {
        '{name}': 'Budi Santoso',
        '{account_number}': 'CUST-001',
        '{mikrotik_user}': 'budi001',
        '{mikrotik_password}': 'password123',
        '{service_plan}': 'Paket Premium 50 Mbps',
        '{monthly_fee}': 'Rp 300.000',
        '{branch_name}': 'DEPOK',
        '{branch_phone}': '08888200888'
      },
      invoice: {
        '{customer_name}': 'Budi Santoso',
        '{account_number}': 'CUST-001',
        '{invoice_number}': 'INV-25111800001',
        '{period}': 'November 2025',
        '{due_date}': '25 November 2025',
        '{total_amount}': 'Rp 300.000',
        '{service_plan}': 'Paket Premium 50 Mbps'
      },
      reminder: {
        '{name}': 'Budi Santoso',
        '{account_number}': 'CUST-001',
        '{due_date}': '25 November 2025',
        '{amount_due}': 'Rp 300.000',
        '{service_plan}': 'Paket Premium 50 Mbps'
      },
      overdue: {
        '{name}': 'Budi Santoso',
        '{account_number}': 'CUST-001',
        '{due_date}': '25 November 2025',
        '{days_overdue}': '3 hari',
        '{amount_due}': 'Rp 300.000',
        '{service_plan}': 'Paket Premium 50 Mbps'
      },
      confirmation: {
        '{customer_name}': 'Budi Santoso',
        '{account_number}': 'CUST-001',
        '{payment_number}': 'PAY-25111800001',
        '{payment_date}': '18 November 2025',
        '{payment_method}': 'Transfer Bank',
        '{period}': 'November 2025',
        '{amount_paid}': 'Rp 300.000'
      }
    };
    return samples[templateType] || {};
  };

  const previewTemplate = (templateType) => {
    const template = templates[templateType];
    if (!template) return;

    const sampleData = getSampleData(templateType);
    let previewSubject = template.subject;
    let previewBody = template.body;

    // Replace all variables with sample data
    Object.entries(sampleData).forEach(([variable, value]) => {
      const regex = new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      previewSubject = previewSubject.replace(regex, value);
      previewBody = previewBody.replace(regex, value);
    });

    setPreviewData({
      subject: previewSubject,
      body: previewBody,
      type: templateType
    });
    setPreviewOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-slate-300">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6 dark:bg-slate-800 dark:border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg dark:bg-indigo-900/40">
              <Mail className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">
                Email / SMTP Configuration
              </h1>
              <p className="text-sm text-gray-500 mt-0.5 dark:text-slate-400">
                Configure SMTP settings to send email notifications to customers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-6">

        {alert.show && (
          <Alert className={`mb-6 ${alert.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <AlertDescription className={`flex items-center gap-2 ${alert.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {alert.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {alert.message}
            </AlertDescription>
          </Alert>
        )}

        {/* SMTP Configuration */}
        <Card className="mb-6 border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <CardTitle className="text-lg font-semibold">SMTP Server Configuration</CardTitle>
              </div>
              {/* Active/Inactive Toggle */}
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${config.is_active ? 'text-green-600' : 'text-gray-500'}`}>
                  {config.is_active ? 'Aktif' : 'Tidak Aktif'}
                </span>
                <button
                  onClick={() => setConfig({ ...config, is_active: !config.is_active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    config.is_active ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <CardDescription className="text-sm mt-1.5">
              Configure your email server settings (currently using: mail.kumara-hotspot.net)
            </CardDescription>
            {/* Status Badge */}
            <div className="mt-2">
              {config.is_active ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                  SMTP Server Aktif - Email notifications enabled
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                  SMTP Server Tidak Aktif - Email notifications disabled
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-5 pt-0">
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="host" className="text-sm font-medium text-gray-700 dark:text-slate-200">SMTP Host *</Label>
                <Input
                  id="host"
                  placeholder="mail.kumara-hotspot.net"
                  value={config.host}
                  onChange={(e) => setConfig({ ...config, host: e.target.value })}
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="port" className="text-sm font-medium text-gray-700 dark:text-slate-200">SMTP Port *</Label>
                <Input
                  id="port"
                  type="number"
                  placeholder="465"
                  value={config.port}
                  onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) })}
                  className="h-10"
                />
                <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Port 465 (SSL) or 587 (TLS)</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium text-gray-700 dark:text-slate-200">Email / Username *</Label>
                <Input
                  id="username"
                  type="email"
                  placeholder="support@kumara-hotspot.net"
                  value={config.username}
                  onChange={(e) => setConfig({ ...config, username: e.target.value })}
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-slate-200">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  className="h-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="from_name" className="text-sm font-medium text-gray-700 dark:text-slate-200">From Name</Label>
              <Input
                id="from_name"
                placeholder="Nama Perusahaan"
                value={config.from_name}
                onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
                className="h-10"
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Name that appears as the sender</p>
            </div>

            <div className="flex items-center gap-6 py-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.use_ssl}
                  onChange={(e) => setConfig({ ...config, use_ssl: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:text-indigo-400"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Use SSL/TLS</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:text-indigo-400"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Enable Email Notifications</span>
              </label>
            </div>

            <div className="pt-5 border-t border-gray-200 dark:border-slate-700">
              <Button
                onClick={saveConfig}
                disabled={saving}
                className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-6"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Test Email */}
        <Card className="mb-6 border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <CardTitle className="text-lg font-semibold">Test Email</CardTitle>
            </div>
            <CardDescription className="text-sm mt-1.5">
              Send a test email to verify your SMTP configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 pt-0">
            {/* Template Type Selector */}
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg dark:bg-indigo-900/30">
              <Label htmlFor="template_type" className="text-sm font-semibold text-indigo-900 mb-2 block">📧 Select Email Template to Test</Label>
              <select
                id="template_type"
                className="w-full h-10 px-3 text-sm border border-indigo-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-800"
                value={testEmail.template_type}
                onChange={(e) => setTestEmail({ ...testEmail, template_type: e.target.value })}
              >
                <option value="">Simple Test Email (Default)</option>
                <option value="welcome">✉️ Welcome Email - New Customer Registration</option>
                <option value="invoice">📄 Invoice Email - Monthly Bill</option>
                <option value="reminder">⏰ Payment Reminder - Due Date Alert</option>
                <option value="overdue">⚠️ Payment Overdue - Late Payment Notice</option>
                <option value="confirmation">✅ Payment Confirmation - Payment Received</option>
              </select>
              <p className="text-xs text-indigo-700 mt-2 dark:text-indigo-300">
                💡 {testEmail.template_type 
                  ? 'Template akan dikirim dengan data contoh (sample data)' 
                  : 'Kirim email test sederhana untuk verifikasi SMTP'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="test_email" className="text-sm font-medium text-gray-700 dark:text-slate-200">Recipient Email *</Label>
              <Input
                id="test_email"
                type="email"
                placeholder="test@example.com"
                value={testEmail.to_email}
                onChange={(e) => setTestEmail({ ...testEmail, to_email: e.target.value })}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="test_subject" className="text-sm font-medium text-gray-700 dark:text-slate-200">Subject</Label>
              <Input
                id="test_subject"
                placeholder="Test Email"
                value={testEmail.subject}
                onChange={(e) => setTestEmail({ ...testEmail, subject: e.target.value })}
                className="h-10"
                disabled={!!testEmail.template_type}
              />
              {testEmail.template_type && (
                <p className="text-xs text-gray-500 dark:text-slate-400">Subject akan diganti dengan subject template yang dipilih</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="test_message" className="text-sm font-medium text-gray-700 dark:text-slate-200">Message</Label>
              <textarea
                id="test_message"
                rows={4}
                className="w-full p-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none dark:border-slate-600"
                placeholder="Test message content..."
                value={testEmail.message}
                onChange={(e) => setTestEmail({ ...testEmail, message: e.target.value })}
                disabled={!!testEmail.template_type}
              />
              {testEmail.template_type && (
                <p className="text-xs text-gray-500 dark:text-slate-400">Message akan diganti dengan content template yang dipilih dengan sample data</p>
              )}
            </div>

            <div className="pt-2">
              <Button
                onClick={sendTestEmail}
                disabled={testLoading || !config.enabled}
                className="h-10 bg-green-600 hover:bg-green-700 text-white font-medium px-6"
              >
                {testLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </Button>
            </div>

            {!config.enabled && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/30">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0 dark:text-amber-400" />
                <p className="text-sm text-amber-800">
                  Email notifications are currently <strong>disabled</strong>. Please enable them in the configuration above to send test emails.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Notification Types */}
        <Card className="mb-6 border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold">📨 Email Notification Types</CardTitle>
            <CardDescription className="text-sm mt-1.5">
              The following email notifications will be sent automatically:
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-1.5 text-sm">✉️ Welcome Email</h4>
                <p className="text-xs text-green-700 leading-relaxed dark:text-green-300">Sent when a new customer is registered (includes username, password, package info)</p>
              </div>

              <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-1.5 text-sm">📄 Invoice Email</h4>
                <p className="text-xs text-blue-700 leading-relaxed dark:text-blue-300">Monthly invoice with PDF attachment</p>
              </div>

              <div className="p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg">
                <h4 className="font-semibold text-yellow-900 mb-1.5 text-sm">⏰ Payment Reminder</h4>
                <p className="text-xs text-yellow-700 leading-relaxed dark:text-yellow-300">Sent H-5 before due date</p>
              </div>

              <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg">
                <h4 className="font-semibold text-red-900 mb-1.5 text-sm">⚠️ Payment Overdue</h4>
                <p className="text-xs text-red-700 leading-relaxed dark:text-red-300">Sent after payment due date passes</p>
              </div>

              <div className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-1.5 text-sm">✅ Payment Confirmation</h4>
                <p className="text-xs text-purple-700 leading-relaxed dark:text-purple-300">Sent after customer makes payment</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Template Editor */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <CardTitle className="text-lg font-semibold">Customize Email Templates</CardTitle>
            </div>
            <CardDescription className="text-sm mt-1.5">
              Edit subject dan body HTML untuk setiap template email. Gunakan variabel yang tersedia.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="ml-3 text-sm text-gray-600 dark:text-slate-300">Loading templates...</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Info box */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/30">
                  <h4 className="font-semibold text-blue-900 mb-2 text-sm">💡 Tips Penggunaan</h4>
                  <ul className="text-xs text-blue-700 space-y-1.5 list-disc list-inside leading-relaxed dark:text-blue-300">
                    <li>Gunakan variabel seperti <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs dark:bg-blue-900/40">{'{nama}'}</code> untuk data dinamis</li>
                    <li>Body mendukung HTML untuk styling (contoh: <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs dark:bg-blue-900/40">&lt;strong&gt;tebal&lt;/strong&gt;</code>)</li>
                    <li>Klik "Reset ke Default" untuk mengembalikan template asli</li>
                    <li>Variabel yang tersedia ditampilkan di setiap template</li>
                  </ul>
                </div>

                {/* Template List */}
                {Object.keys(templates).length > 0 ? (
                  Object.entries(templates).map(([type, template]) => {
                    const isExpanded = expandedTemplate === type;
                    const isSaving = savingTemplate === type;
                  
                  // Define colors based on template type
                  const colorClasses = {
                    welcome: {
                      border: 'border-green-200',
                      bg: 'bg-gradient-to-r from-green-50 to-green-100',
                      hover: 'hover:from-green-100 hover:to-green-150',
                      title: 'text-green-900',
                      subtitle: 'text-green-700'
                    },
                    invoice: {
                      border: 'border-blue-200',
                      bg: 'bg-gradient-to-r from-blue-50 to-blue-100',
                      hover: 'hover:from-blue-100 hover:to-blue-150',
                      title: 'text-blue-900',
                      subtitle: 'text-blue-700'
                    },
                    reminder: {
                      border: 'border-yellow-200',
                      bg: 'bg-gradient-to-r from-yellow-50 to-yellow-100',
                      hover: 'hover:from-yellow-100 hover:to-yellow-150',
                      title: 'text-yellow-900',
                      subtitle: 'text-yellow-700'
                    },
                    overdue: {
                      border: 'border-red-200',
                      bg: 'bg-gradient-to-r from-red-50 to-red-100',
                      hover: 'hover:from-red-100 hover:to-red-150',
                      title: 'text-red-900',
                      subtitle: 'text-red-700'
                    },
                    confirmation: {
                      border: 'border-purple-200',
                      bg: 'bg-gradient-to-r from-purple-50 to-purple-100',
                      hover: 'hover:from-purple-100 hover:to-purple-150',
                      title: 'text-purple-900',
                      subtitle: 'text-purple-700'
                    }
                  };
                  
                  const colors = colorClasses[type] || colorClasses.welcome;

                    return (
                      <div key={type} className={`border ${colors.border} rounded-lg overflow-hidden shadow-sm`}>
                        {/* Template Header (Collapsible) */}
                        <div 
                          className={`p-4 ${colors.bg} cursor-pointer flex items-center justify-between ${colors.hover} transition-colors`}
                          onClick={() => toggleTemplateExpand(type)}
                        >
                          <div className="flex-1">
                            <h4 className={`font-semibold text-sm ${colors.title}`}>{getTemplateName(type)}</h4>
                            <p className={`text-xs ${colors.subtitle} mt-1`}>
                              Subject: {template.subject}
                            </p>
                          </div>
                          <div className="ml-4">
                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </div>
                        </div>

                        {/* Template Editor (Expanded) */}
                        {isExpanded && (
                          <div className="p-5 bg-white space-y-5 border-t border-gray-200 dark:bg-slate-800 dark:border-slate-700">
                            {/* Available Variables */}
                            {template.variables && template.variables.length > 0 && (
                              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg dark:bg-slate-900 dark:border-slate-700">
                                <h5 className="text-xs font-semibold text-gray-700 mb-2 dark:text-slate-200">📝 Variabel Tersedia:</h5>
                                <div className="flex flex-wrap gap-2">
                                  {template.variables.map((v, idx) => (
                                    <span 
                                      key={idx}
                                      className="inline-flex items-center px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono dark:bg-slate-800 dark:border-slate-600"
                                      title={v.description}
                                    >
                                      {v.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Subject Field */}
                            <div className="space-y-2">
                              <Label htmlFor={`subject-${type}`} className="text-sm font-medium text-gray-700 dark:text-slate-200">Subject Email *</Label>
                              <Input
                                id={`subject-${type}`}
                                placeholder="Subject email..."
                                value={template.subject || ''}
                                onChange={(e) => updateTemplate(type, 'subject', e.target.value)}
                                className="h-10"
                              />
                            </div>

                            {/* Body Field */}
                            <div className="space-y-2">
                              <Label htmlFor={`body-${type}`} className="text-sm font-medium text-gray-700 dark:text-slate-200">Body HTML *</Label>
                              <textarea
                                id={`body-${type}`}
                                rows={12}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm resize-none dark:border-slate-600"
                                placeholder="HTML template body..."
                                value={template.body || ''}
                                onChange={(e) => updateTemplate(type, 'body', e.target.value)}
                              />
                              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                                Character count: {(template.body || '').length}
                              </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-2">
                              <Button
                                onClick={() => saveTemplate(type)}
                                disabled={isSaving}
                                className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-5"
                              >
                                {isSaving ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Menyimpan...
                                  </>
                                ) : (
                                  <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Simpan Template
                                  </>
                                )}
                              </Button>

                              <Button
                                onClick={() => previewTemplate(type)}
                                disabled={isSaving}
                                variant="outline"
                                className="h-10 border-green-300 text-green-700 hover:bg-green-50 font-medium px-5 dark:text-green-300 dark:hover:bg-green-900/30"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Preview
                              </Button>

                              <Button
                                onClick={() => resetTemplate(type)}
                                disabled={isSaving}
                                variant="outline"
                                className="h-10 border-red-300 text-red-700 hover:bg-red-50 font-medium px-5 dark:text-red-300 dark:hover:bg-red-900/30"
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Reset ke Default
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12">
                    <p className="text-sm text-gray-500 dark:text-slate-400">No templates available</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col dark:bg-slate-800">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-2">
                <Eye className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">Preview Email Template</h3>
              </div>
              <button
                onClick={() => setPreviewOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Subject Preview */}
              <div className="mb-6">
                <Label className="text-sm font-semibold text-gray-700 mb-2 block dark:text-slate-200">Subject:</Label>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg dark:bg-slate-900 dark:border-slate-700">
                  <p className="text-gray-900 font-medium dark:text-slate-100">{previewData.subject}</p>
                </div>
              </div>

              {/* Body Preview */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block dark:text-slate-200">Body:</Label>
                <div className="border-2 border-gray-200 rounded-lg bg-white p-6 min-h-[400px] dark:border-slate-700 dark:bg-slate-800">
                  {/* Render HTML with sample data */}
                  <div 
                    dangerouslySetInnerHTML={{ __html: previewData.body }}
                    className="prose max-w-none"
                  />
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/30">
                <p className="text-sm text-blue-800">
                  💡 <strong>Preview ini menggunakan data contoh.</strong> Email yang dikirim ke customer akan menggunakan data real sesuai dengan customer dan transaksi mereka.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-2 dark:bg-slate-900">
              <Button
                onClick={() => setPreviewOpen(false)}
                variant="outline"
              >
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default EmailSMTP;
